import React from 'react';
import { Check, DollarSign, AlertCircle, Lock } from 'lucide-react';

/**
 * Sidebar Component
 * Contains progress steps and price summary
 * 
 * ⭐ H2: Enhanced with step completion status and locked step indicators
 * 
 * @param {Object} props
 * @param {Array} props.steps - Array of step objects { id, name, icon }
 * @param {number} props.currentStep - Current active step index
 * @param {Function} props.onStepClick - Handler for step navigation
 * @param {Function} props.canNavigateToStep - Function to check if step is accessible
 * @param {Function} props.getStepStatus - Function to check if step is complete
 * @param {Object} props.totals - Calculated totals object
 */
const Sidebar = ({ 
  steps, 
  currentStep, 
  onStepClick, 
  canNavigateToStep,
  getStepStatus,
  totals 
}) => {
  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
      {/* Progress Section */}
      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
        Progress
      </h3>
      
      {/* Step Navigation */}
      <div className="space-y-3">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          const isActive = currentStep === idx;
          const isComplete = currentStep > idx;
          const canNavigate = canNavigateToStep(idx);
          const isStepValid = getStepStatus ? getStepStatus(idx) : true;
          
          // ⭐ H2: Determine if step is locked (can't navigate to it)
          const isLocked = !canNavigate && idx > currentStep;
          
          // ⭐ H2: Determine if step needs attention (current or past but incomplete)
          const needsAttention = !isStepValid && idx <= currentStep;
          
          return (
            <button
              key={step.id}
              onClick={() => onStepClick(idx)}
              disabled={!canNavigate}
              className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                isActive 
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-200' 
                  : isComplete && isStepValid
                  ? 'bg-green-50 text-green-700 hover:bg-green-100'
                  : isComplete && !isStepValid
                  ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                  : canNavigate
                  ? 'text-slate-500 hover:bg-slate-50 cursor-pointer'
                  : 'text-slate-300 cursor-not-allowed opacity-50'
              }`}
              title={isLocked ? 'Complete previous steps to unlock' : ''}
            >
              {/* Step Icon/Status */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                isActive 
                  ? 'bg-white/20' 
                  : isComplete && isStepValid
                  ? 'bg-green-100'
                  : needsAttention
                  ? 'bg-amber-100'
                  : isLocked
                  ? 'bg-slate-100'
                  : 'bg-slate-100'
              }`}>
                {isLocked ? (
                  <Lock size={16} className="text-slate-400" />
                ) : isComplete && isStepValid ? (
                  <Check size={18} className="text-green-600" />
                ) : needsAttention ? (
                  <AlertCircle size={18} className="text-amber-600" />
                ) : (
                  <Icon size={18} className={isActive ? 'text-white' : 'text-slate-400'} />
                )}
              </div>
              
              {/* Step Name */}
              <span className="font-medium text-sm flex-1 text-left">{step.name}</span>
              
              {/* ⭐ H2: Status indicator badge */}
              {!isActive && needsAttention && (
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">
                  Incomplete
                </span>
              )}
              {isLocked && (
                <span className="text-xs bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">
                  Locked
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ⭐ H2: Validation Summary */}
      <ValidationSummary 
        steps={steps}
        currentStep={currentStep}
        getStepStatus={getStepStatus}
      />

      {/* Price Summary Section */}
      <PriceSummary totals={totals} />
    </div>
  );
};

/**
 * ⭐ H2: Validation Summary Sub-component
 * Shows which required steps need completion
 */
const ValidationSummary = ({ steps, currentStep, getStepStatus }) => {
  if (!getStepStatus) return null;
  
  const requiredSteps = [0, 1, 3]; // Deal Info, Platform, Products
  const requiredStepNames = ['Deal Info', 'Platform', 'Products'];
  
  const incompleteRequired = requiredSteps.filter((idx, i) => 
    !getStepStatus(idx) && idx <= currentStep
  );
  
  if (incompleteRequired.length === 0) return null;
  
  return (
    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
      <div className="flex items-center gap-2 text-amber-700 text-xs font-medium mb-2">
        <AlertCircle size={14} />
        Required steps incomplete:
      </div>
      <ul className="text-xs text-amber-600 space-y-1">
        {incompleteRequired.map((idx, i) => (
          <li key={idx}>• {requiredStepNames[requiredSteps.indexOf(idx)]}</li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Price Summary Sub-component
 * Displays ARR, one-time, discounts, and totals
 * ⭐ Glassmorphic cards for ARR and Services to make them stand out
 */
const PriceSummary = ({ totals }) => (
  <div className="mt-8 pt-6 border-t border-slate-200">
    {/* Subtotals */}
    <div className="space-y-2 mb-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">ARR Subtotal</span>
        <span className="font-semibold">${totals.arrSubtotal.toLocaleString()}</span>
      </div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">One-Time</span>
        <span className="font-semibold">${totals.oneTimeSubtotal.toLocaleString()}</span>
      </div>
      {totals.totalDiscount > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-red-600">Discount</span>
          <span className="font-semibold text-red-600">-${totals.totalDiscount.toLocaleString()}</span>
        </div>
      )}
    </div>

    {/* ⭐ Glassmorphic Total ARR Card */}
    <div className="relative mb-4 p-4 rounded-2xl overflow-hidden
      bg-gradient-to-br from-emerald-500/90 to-green-600/90
      backdrop-blur-xl shadow-xl shadow-green-500/25
      border border-white/20">
      {/* Glass shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-white/80">Total ARR</span>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <DollarSign size={16} className="text-white" />
          </div>
        </div>
        <div className="text-3xl font-bold text-white tracking-tight">
          ${totals.totalARR.toLocaleString()}
        </div>
        <div className="text-xs text-white/70 mt-1">Annual Recurring Revenue</div>
      </div>
    </div>

    {/* ⭐ Glassmorphic Services Card */}
    <div className="relative p-4 rounded-2xl overflow-hidden
      bg-gradient-to-br from-blue-500/90 to-indigo-600/90
      backdrop-blur-xl shadow-xl shadow-blue-500/25
      border border-white/20">
      {/* Glass shine effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-white/80">Services (OTR)</span>
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <DollarSign size={16} className="text-white" />
          </div>
        </div>
        <div className="text-3xl font-bold text-white tracking-tight">
          ${totals.totalOneTime.toLocaleString()}
        </div>
        <div className="text-xs text-white/70 mt-1">One-Time Revenue</div>
      </div>
    </div>
  </div>
);

export default Sidebar;
